import { call, put } from 'redux-saga/effects';
import { AxiosResponse } from 'axios';
import {
  uploadFileToStrapi,
  createFileUploadRecord,
  getFilesForPrescription,
  deleteFileUploadRecord,
  deleteFileFromStrapi,
  createLicenseFileUploadRecord,
  getFilesForLicense,
  deleteLicenseFileUploadRecord,
  BACKEND_URL,
} from '../../api/api';
import { s3cloudUrl } from 'src/api/config';
import {
  uploadFileSuccess,
  uploadFileFailure,
  uploadFileProgress,
  fetchFilesSuccess,
  fetchFilesFailure,
  deleteFileSuccess,
  deleteFileFailure,
  downloadFileSuccess,
  downloadFileFailure,
} from './actions';

export function* uploadFileSaga(action: any) {
  const { file, prescID, licensesID, tempId, entityType } = action.payload;
  try {
    const uploadResponse: AxiosResponse<any> = yield call(uploadFileToStrapi, file, (progress: number) => {
      put(uploadFileProgress(progress, tempId));
    });

    const uploadedFile = uploadResponse.data[0];

    let recordResponse: AxiosResponse<any>;

    if (entityType === 'license' && licensesID) {
      recordResponse = yield call(createLicenseFileUploadRecord, uploadedFile.id, licensesID);
    } else if (entityType === 'prescription' && prescID) {
      recordResponse = yield call(createFileUploadRecord, uploadedFile.id, prescID);
    } else {
      throw new Error('Invalid entity type or missing ID');
    }

    const recordData = recordResponse.data.data;

    const fileInfo = {
      id: recordData.id,
      documentId: recordData.documentId,
      name: uploadedFile.name,
      url: uploadedFile.url,
      size: uploadedFile.size,
      mime: uploadedFile.mime,
      prescID: entityType === 'prescription' ? prescID : undefined,
      licensesID: entityType === 'license' ? licensesID : undefined,
      uploadId: uploadedFile.id,
      progress: 100,
      isUploading: false,
      tempId: tempId,
    };

    yield put(uploadFileSuccess(fileInfo));
  } catch (error) {
    const errorMessage = (error as any)?.message || 'Ошибка загрузки файла';
    yield put(uploadFileFailure(errorMessage, tempId));
  }
}

export function* fetchFilesSaga(action: any) {
  const { prescID, licensesID, entityType } = action.payload;
  try {
    let response: AxiosResponse<any>;

    if (entityType === 'license' && licensesID) {
      response = yield call(getFilesForLicense, licensesID);
    } else if (entityType === 'prescription' && prescID) {
      response = yield call(getFilesForPrescription, prescID);
    } else {
      throw new Error('Invalid entity type or missing ID');
    }

    const files = response.data.data
      .map((item: any) => {
        const fileData = Array.isArray(item.Name) ? item.Name[0] : item.Name;

        if (!fileData || !fileData.name || !fileData.url) {
          return null;
        }

        const fileName = fileData.name;
        const fileUrl = fileData.url;
        const fileSize = fileData.size || 0;
        const fileMime = fileData.mime || '';
        const fileId = fileData.id;

        return {
          id: item.id,
          documentId: item.documentId,
          name: fileName,
          url: fileUrl,
          size: fileSize,
          mime: fileMime,
          prescID: item.PrescID,
          licensesID: item.LicensesID,
          uploadId: fileId,
          progress: 100,
          isUploading: false,
        };
      })
      .filter((file: any) => file !== null); 

    yield put(fetchFilesSuccess(files));
  } catch (error) {
    const errorMessage = (error as any)?.message || 'Ошибка загрузки списка файлов';
    yield put(fetchFilesFailure(errorMessage));
  }
}

export function* deleteFileSaga(action: any) {
  const { documentId, uploadId, entityType, prescID, licensesID } = action.payload;
  try {
    if (entityType === 'license') {
      yield call(deleteLicenseFileUploadRecord, documentId);
    } else {
      yield call(deleteFileUploadRecord, documentId);
    }
    yield call(deleteFileFromStrapi, uploadId);
    yield put(deleteFileSuccess(documentId, uploadId));

    if ((prescID || licensesID) && entityType) {
      yield call(fetchFilesSaga, {
        payload: { prescID, licensesID, entityType }
      });
    }
  } catch (error) {
    const errorMessage = (error as any)?.message || 'Ошибка удаления файла';
    yield put(deleteFileFailure(errorMessage));
  }
}

const isCloudLink = (str: string) => {
  return str.includes(s3cloudUrl);
};

export function* downloadFileSaga(action: any) {
  const { url, name } = action.payload;
  try {
    const fileUrl = isCloudLink(url)
      ? `${BACKEND_URL}/${new URL(url).pathname.slice(1)}`
      : `${BACKEND_URL}${url}`;

    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = name || 'downloaded-file';
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    yield put(downloadFileSuccess());
  } catch (error) {
    const errorMessage = (error as any)?.message || 'Ошибка скачивания файла';
    yield put(downloadFileFailure(errorMessage));
  }
}
